import React, { useState } from 'react';
import { Container, Typography, Box, Paper, TextField, Button, Grid, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // For now, just show success message. Can be replaced with API call later.
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: '', email: '', subject: '', message: '' });
    }, 5000);
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            {t('contact.label')}
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('contact.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: '560px' }}>
            {t('contact.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
              {t('contact.reachUs')}
            </Typography>

            <Paper elevation={0} sx={{ p: 3.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                ✉️ {t('contact.email')}
              </Typography>
              <Typography
                component="a"
                href="mailto:contact@indeeclips.com"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                contact@indeeclips.com
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 3.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                🌐 {t('contact.website')}
              </Typography>
              <Typography
                component="a"
                href="https://indeeclips.com"
                target="_blank"
                rel="noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                https://indeeclips.com
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 3.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                📱 {t('contact.androidApp')}
              </Typography>
              <Typography
                component="a"
                href="https://play.google.com/store/apps/details?id=com.ind.indeeclips"
                target="_blank"
                rel="noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Download on Google Play
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 3.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                🍎 {t('contact.iosApp')}
              </Typography>
              <Typography
                component="a"
                href="https://apps.apple.com/in/app/indeeclips/id6748682495"
                target="_blank"
                rel="noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Download on App Store
              </Typography>
            </Paper>

            <Box sx={{ mt: 5 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {t('contact.forCreators')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
                Want to upload your content on IndeeClips and reach millions of Indian viewers? Contact us at{' '}
                <Typography
                  component="a"
                  href="mailto:contact@indeeclips.com"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  contact@indeeclips.com
                </Typography>
              </Typography>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {t('contact.forAdvertisers')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
                Interested in advertising on IndeeClips and reaching 2,000+ users? Reach out to us at{' '}
                <Typography
                  component="a"
                  href="mailto:contact@indeeclips.com"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  contact@indeeclips.com
                </Typography>
              </Typography>
            </Box>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
              {t('contact.sendMessage')}
            </Typography>

            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('contact.successMessage')}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('contact.name')}
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label={t('contact.emailAddress')}
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label={t('contact.subject')}
                name="subject"
                value={form.subject}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label={t('contact.message')}
                name="message"
                multiline
                rows={6}
                value={form.message}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
              />
              <Button type="submit" variant="contained" size="large" fullWidth sx={{ textTransform: 'none', py: 1.5 }}>
                {t('contact.submitButton')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
